import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckOutlined, MinusOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { PLANS, getPlanPricing, getPlanLink } from '../constants/plans';
// Thème citron / noir / blanc de la page (classes préfixées "pr-")
import './Pricing.css';

/**
 * Ligne du tableau comparatif : une valeur par offre, dans l'ordre de `PLANS`.
 * `true` / `false` affichent une coche ou un tiret, une chaîne s'affiche telle quelle.
 * @param {string} category Rubrique.
 * @param {string} feature Libellé de la ligne.
 * @param {Array<string|boolean>} values Valeurs pour Découverte, Starter, Essentiel, Croissance, Excellence.
 */
const row = (category, feature, values) => ({
  category,
  feature,
  ...Object.fromEntries(PLANS.map((plan, index) => [plan.key, values[index]]))
});

// Données du tableau comparatif (quotas identiques à ceux de server/config/plans.config.js)
const COMPARISON = [
  // Communications WhatsApp
  row('Communications WhatsApp', 'Messages WhatsApp', ['20 au total', '100 / mois', '300 / mois', '1 000 / mois', 'Illimités']),
  row('Communications WhatsApp', 'Modèles de message', ['2', '5', '20', '50', 'Illimités']),
  row('Communications WhatsApp', 'Connexion via fournisseur API', [true, true, true, true, true]),

  // Gestion boutiques
  row('Gestion boutiques', 'Produits actifs', ['10', '50', '200', '1 000', 'Illimités']),
  row('Gestion boutiques', 'Commandes', ['15 au total', '80 / mois', '250 / mois', '800 / mois', 'Illimitées']),
  row('Gestion boutiques', 'Utilisateurs', ['1', '1', '3', '6', 'Illimités']),
  row('Gestion boutiques', 'Gestion des caissiers', [false, false, false, true, true]),

  // Intelligence artificielle
  row('Intelligence artificielle', 'Points IA', ['30 offerts', '150 / mois', '500 / mois', '1 200 / mois', '3 000 / mois']),
  row('Intelligence artificielle', 'Assistant commercial', [true, true, true, true, true]),
  row('Intelligence artificielle', 'Fiches produit et images générées', [true, true, true, true, true]),
  row('Intelligence artificielle', 'Recharge de points à l’unité', [true, true, true, true, true]),

  // Point de Vente
  row('Point de Vente', 'Interface POS', [false, false, false, true, true]),
  row('Point de Vente', 'Lecture code-barres', [false, false, false, true, true]),
  row('Point de Vente', 'Gestion variantes produits', [false, false, false, true, true]),
  row('Point de Vente', 'Génération reçus WhatsApp', [false, false, false, true, true]),

  // Livraisons
  row('Livraisons', 'Nombre de livreurs', ['1', '2', '5', '15', 'Illimités']),
  row('Livraisons', 'Zones de livraison', ['1', '2', '5', '15', 'Illimitées']),
  row('Livraisons', 'Suivi des livraisons', [false, true, true, true, true]),
  row('Livraisons', 'Statistiques livreurs', [false, false, 'Basiques', 'Avancées', 'Personnalisées']),
  row('Livraisons', 'Attribution automatique', [false, false, false, true, true]),

  // Analytics
  row('Analytics', 'Rapports de ventes', [false, false, 'Standards', 'Avancés + exports', 'Personnalisables']),
  row('Analytics', 'Tableaux de bord', [false, false, 'Basique', 'Avancé', 'Personnalisé']),
  row('Analytics', 'Campagnes marketing', [false, false, false, true, true]),
  row('Analytics', 'Programme de fidélité', [false, false, false, true, true]),

  // Support
  row('Support', 'Type de support', ['Documentation', 'Email', 'Email (48h)', 'Prioritaire (24h)', 'Dédié']),
  row('Support', 'Formation', [false, false, false, 'Documentation', 'Personnalisée']),

  // Avancé
  row('Avancé', 'API pour développeurs', [false, false, false, false, 'Complète']),
  row('Avancé', 'Intégrations sur mesure', [false, false, false, false, true]),
  row('Avancé', 'Marque blanche', [false, false, false, false, true])
];

// Questions fréquentes (réponses alignées sur le fonctionnement réel de l'application)
const FAQ = [
  {
    question: 'Puis-je changer d\'offre à tout moment ?',
    reponse: 'Oui, vous pouvez passer à une offre supérieure à tout moment. Le changement est appliqué immédiatement et l\'enveloppe de points IA de la nouvelle offre est chargée dès la lecture suivante.'
  },
  {
    question: 'Y a-t-il une période d\'essai gratuite ?',
    reponse: 'Oui : pendant 14 jours, vous accédez aux fonctionnalités de l\'offre Essentiel avec les quotas de Découverte (20 messages, 15 commandes). À la fin de l\'essai, votre compte repasse automatiquement sur Découverte.'
  },
  {
    question: 'Que se passe-t-il à la fin de l\'essai ?',
    reponse: 'Après 14 jours, votre compte repasse automatiquement sur Découverte, avec ses quotas non renouvelables (20 messages WhatsApp et 15 commandes au total). Pour continuer, choisissez une offre payante à partir de 5 000 XOF par mois (Starter).'
  },
  {
    question: 'Les quotas de Découverte se renouvellent-ils ?',
    reponse: 'Non. Découverte offre 20 messages WhatsApp, 15 commandes et 30 points IA au total. Ces quotas ne se renouvellent pas.'
  },
  {
    question: 'Comment payer mon abonnement ?',
    reponse: 'Le paiement se fait en ligne sur la page de paiement sécurisée Chariow. L\'offre n\'est activée qu\'une fois le paiement confirmé. Pour l\'offre Excellence, contactez-nous.'
  },
  {
    question: 'Comment fonctionnent les points IA ?',
    reponse: 'Chaque offre inclut une enveloppe de points IA rechargée chaque mois, sans report du reliquat. Elle sert à l\'assistant commercial, aux fiches produit et aux images générées. Vous pouvez acheter des points supplémentaires à tout moment ; ceux-ci se cumulent sans limite de temps.'
  }
];

/**
 * Rend une cellule du comparatif : coche, tiret ou texte.
 * @param {string|boolean} value Valeur de la cellule.
 */
const renderCell = (value) => {
  if (value === false) return <MinusOutlined className="pr-no" aria-label="Non inclus" />;
  if (value === true) return <CheckOutlined className="pr-yes" aria-label="Inclus" />;
  return <span>{value}</span>;
};

const Pricing = () => {
  // Période d'affichage : mensuel ou annuel (deux mois offerts)
  const [annually, setAnnually] = useState(false);

  // Regroupe les lignes du comparatif par rubrique
  const grouped = COMPARISON.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  return (
    <div className="pr-page">
      {/* Hero et choix de la période */}
      <section className="pr-hero">
        <span className="pr-pill">Tarifs</span>
        <h1>Des offres claires, <span className="pr-mark">pour chaque étape</span></h1>
        <p>Commencez gratuitement avec l'essai de 14 jours, puis évoluez selon vos besoins.</p>
        <div className="pr-toggle" role="group" aria-label="Période de facturation">
          <button type="button" className={!annually ? 'pr-on' : ''} aria-pressed={!annually} onClick={() => setAnnually(false)}>Mensuel</button>
          <button type="button" className={annually ? 'pr-on' : ''} aria-pressed={annually} onClick={() => setAnnually(true)}>
            Annuel <small>-16 %</small>
          </button>
        </div>
      </section>

      {/* Cartes des offres */}
      <section className="pr-plans">
        {PLANS.map((plan) => {
          const prix = getPlanPricing(plan, annually);
          return (
            <article key={plan.key} className={`pr-plan ${plan.popular ? 'pr-plan-pop' : ''}`}>
              {plan.popular && <span className="pr-badge">Le plus populaire</span>}
              <h2>{plan.name}</h2>
              <p className="pr-plan-desc">{plan.description}</p>
              <div className="pr-price">
                <strong>{prix.price}</strong>
                {prix.period && <span>{prix.period}</span>}
              </div>
              {prix.save && <small className="pr-save">{prix.save}</small>}
              <Link to={getPlanLink(plan)} className={`pr-btn ${plan.popular ? 'pr-btn-accent' : 'pr-btn-outline'}`}>
                {plan.buttonText} <ArrowRightOutlined />
              </Link>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}><CheckOutlined /> {feature}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </section>

      {/* Tableau comparatif détaillé (défilement horizontal sur mobile) */}
      <section className="pr-compare">
        <h2>Comparaison détaillée</h2>
        <div className="pr-table-wrap">
          <table className="pr-table">
            <thead>
              <tr>
                <th>Fonctionnalité</th>
                {PLANS.map((plan) => <th key={plan.key}>{plan.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([category, items]) => (
                <React.Fragment key={category}>
                  <tr className="pr-cat"><th colSpan={PLANS.length + 1}>{category}</th></tr>
                  {items.map((item) => (
                    <tr key={`${category}-${item.feature}`}>
                      <th scope="row">{item.feature}</th>
                      {PLANS.map((plan) => <td key={plan.key}>{renderCell(item[plan.key])}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="pr-faq">
        <h2>Questions fréquentes</h2>
        <div className="pr-faq-grid">
          {FAQ.map((item) => (
            <article key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.reponse}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Appel à l'action */}
      <section className="pr-cta">
        <h2>Démarrez votre essai gratuit</h2>
        <p>14 jours pour tester Mercuri Shop avec les fonctionnalités de l'offre Essentiel.</p>
        <div>
          <Link to="/register" className="pr-btn pr-btn-accent">Commencer gratuitement</Link>
          <Link to="/contact" className="pr-btn pr-btn-light">Demander une démo</Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
